import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Button, message} from 'antd';
import {CloseOutlined} from '@ant-design/icons';
import {useSearchFiles, useRecentSearch} from '../../hooks/useSearchFiles';
import {uploadService} from '../../services/upload';
import {uploadToObs} from '../../utils/obs';
import {getObsKeyFromSignedUrl} from '../../utils/file';
import {SearchBox} from './SearchBox';
import {SearchResults} from './SearchResults';
import {TagPickerModal} from './TagPickerModal';
import './index.scss';
import {calcFileMd5} from '../../utils/md5';
import {getFileNameFromObsKey} from '../../utils/filePreview';
import {searchService} from '../../services/search';
import {RecentSearchCard} from './RecentSearchCard';

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
  onResultChange?: (hasResult: boolean) => void;
  onRecentChange?: (hasRecent: boolean) => void;
  onJumpPath: (item: any) => void;
}

export function SearchOverlay({open, onClose, onResultChange, onRecentChange, onJumpPath}: SearchOverlayProps) {
  const [keywords, setKeywords] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [obsKeys, setObsKeys] = useState<string[]>([]);
  const [results, setResults] = useState<any>(null);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [searchFiles, setSearchFiles] = useState<File[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [resultList, setResultList] = useState<any[]>([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const resultContainerRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(false);
  const [hasNext, setHasNext] = useState(false);

  const searchMutation = useSearchFiles();
  const searchMutationRef = useRef(searchMutation);

  const {
    data: recentData,
    refetch: refetchRecentSearch,
  } = useRecentSearch(4, open);

  const recentList = Array.isArray(recentData)
    ? recentData
    : recentData?.data || [];

  useEffect(() => {
    searchMutationRef.current = searchMutation;
  }, [searchMutation]);

  useEffect(() => {
    onResultChange?.(!!results);
  }, [results, onResultChange]);

  useEffect(() => {
    onRecentChange?.(!!recentList.length);
  }, [recentList.length, onRecentChange]);

  const runSearch = useCallback(async ({
                                         pageIndex = 1,
                                         includeFile = true
                                       }) => {
    let finalObsKeys = obsKeys;

    // 图片搜索：只在手动搜索时触发
    if (includeFile && searchFiles.length && !finalObsKeys.length) {
      setUploadingFile(true);

      try {
        const nextObsKeys: string[] = [];

        for (const file of searchFiles) {
          const md5 = await calcFileMd5(file);

          const checkRes = await uploadService.checkFileByMd5({file_md5: md5});

          if (checkRes && typeof checkRes === 'string') {
            nextObsKeys.push(checkRes);
            continue;
          }

          const uploadUrl = await uploadService.getSingleUploadUrl({
            filename: file.name
          });

          await uploadToObs(uploadUrl, file);

          const obsKey = getObsKeyFromSignedUrl(uploadUrl);

          nextObsKeys.push(obsKey);
        }

        finalObsKeys = nextObsKeys;

        setObsKeys(nextObsKeys);
      } finally {
        setUploadingFile(false);
      }
    }

    return searchMutation.mutateAsync({
      obskey: finalObsKeys[0] || '',
      keywords,
      tags,
      page_size: 10,
      page_index: pageIndex,
    });
  }, [obsKeys, searchFiles, keywords, tags, searchMutation]);

  function clearSearchState() {
    setResults(null);
    setResultList([]);
    setPageIndex(1);
    setHasNext(false);
    setLoadingMore(false);
  }

  const resetSearch = useCallback((refreshRecent = true) => {
    clearSearchState();

    if (refreshRecent) {
      void refetchRecentSearch();
    }
  }, [refetchRecentSearch]);

  const handleSearch = useCallback(async () => {
    const hasCondition = keywords.trim() || tags.length || searchFiles.length || obsKeys.length;

    if (!hasCondition) {
      void message.warning('请输入搜索内容或选择文件');
      return;
    }

    const res = await runSearch({
      pageIndex: 1,
      includeFile: true
    });

    const list = res?.results || [];

    setResults(res);
    setResultList(list);
    setPageIndex(1);
    setHasNext(!!res?.has_next);
  }, [keywords, tags, searchFiles, obsKeys, runSearch]);

  const loadMoreResults = useCallback(async () => {
    if (!hasNext) return;
    if (loadingMoreRef.current) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);

    const nextPageIndex = pageIndex + 1;

    try {
      const res = await runSearch({
        pageIndex: nextPageIndex,
        includeFile: false
      });

      const nextList = res?.results || [];

      setResultList(prev => [...prev, ...nextList]);
      setPageIndex(nextPageIndex);
      setHasNext(!!res?.has_next);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [pageIndex, hasNext, runSearch]);

  function handleFileBeforeUpload(file: any) {
    if (searchFiles.length >= 1) {
      void message.warning('当前搜索暂时只支持上传 1 个文件，请先删除已选文件');
      return false;
    }

    // setSearchFiles(prev => [...prev, file]);
    setSearchFiles([file]);
    setObsKeys([]);
    return false;
  }

  function handleClose() {
    clearSearchState();

    setKeywords('');
    setTags([]);
    setSearchFiles([]);
    setObsKeys([]);

    onClose();
  }

  function handlePasteFile(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const items = Array.from(e.clipboardData?.items || []);
    const fileItem = items.find(item => item.kind === 'file');

    if (!fileItem) return;

    const file = fileItem.getAsFile();

    if (!file) return;

    if (searchFiles.length >= 1) {
      void message.warning('当前搜索暂时只支持上传 1 个文件，请先删除已选文件');
      return;
    }

    const nextFile = new File(
      [file],
      file.name || `paste-${Date.now()}.${file.type.split('/')[1] || ''}`,
      {
        type: file.type || 'application/octet-stream'
      }
    );

    setSearchFiles([nextFile]);
    setObsKeys([]);
  }

  useEffect(() => {
    if (!results) return;

    const container = resultContainerRef.current;
    if (!container) return;

    function handleScroll() {
      const {scrollTop, scrollHeight, clientHeight} = container;

      const distanceToBottom = scrollHeight - scrollTop - clientHeight;

      if (distanceToBottom > 200) return;
      if (!hasNext) return;
      if (loadingMoreRef.current) return;

      void loadMoreResults();
    }

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [results, hasNext, loadMoreResults]);

  useEffect(() => {
    if (!open) return;

    void refetchRecentSearch();
  }, [open, refetchRecentSearch]);


  // 文字标签变化自动搜索，暂时禁用
  // useEffect(() => {
  //   if (!open) return;
  //
  //   const hasTextCondition = keywords.trim() || tags.length;
  //
  //   const hasAnyCondition =
  //     keywords.trim() ||
  //     tags.length ||
  //     searchFiles.length ||
  //     obsKeys.length;
  //
  //   if (!hasAnyCondition) {
  //     resetSearch(false);
  //     return;
  //   }
  //
  //   // 文件不自动搜索，必须手动搜索
  //   if (!hasTextCondition) return;
  //
  //   const timer = window.setTimeout(async () => {
  //     const res = await searchMutationRef.current.mutateAsync({
  //       obskey: obsKeys[0] || '',
  //       keywords,
  //       tags,
  //       page_index: 1,
  //       page_size: 10,
  //       flag: similar ? 1 : 0
  //     });
  //
  //     const list = res?.results || [];
  //
  //     setResults(res);
  //     setResultList(list);
  //     setPageIndex(1);
  //     setHasNext(!!res?.has_next);
  //   }, 500);
  //
  //   return () => {
  //     window.clearTimeout(timer);
  //   };
  // }, [open, keywords, tags, similar, obsKeys, searchFiles, searchMutationRef, resetSearch]);

  function handleRemoveFile(file: File) {
    setSearchFiles(prev => {
      const next = prev.filter(item => item !== file);

      const hasAnyCondition =
        keywords.trim() ||
        tags.length ||
        next.length;

      if (!hasAnyCondition) {
        resetSearch();
      }

      return next;
    });
    setObsKeys([]);
  }

  function createHistorySearchFile(obskey: string, filename?: string) {
    const file = new File(
      [''],
      filename || getFileNameFromObsKey(obskey),
      {
        type: 'application/octet-stream'
      }
    ) as File & {
      obskey: string;
      isHistorySearchFile?: boolean;
    };

    file.obskey = obskey;
    file.isHistorySearchFile = true;

    return file;
  }

  async function handleClearHistory() {
    try {
      await searchService.clearRecentSearch();

      void refetchRecentSearch();
    } catch (e) {
      message.error('清空失败');
    }
  }

  async function handleRecentSearch(item: any) {
    const nextTags = item.tags
      ? item.tags.split(',').map((tag: string) => tag.trim()).filter(Boolean)
      : [];

    const nextKeywords = item.keywords || '';
    const nextObsKeys = item.file || '';

    setKeywords(nextKeywords);
    setTags(nextTags);
    setObsKeys(nextObsKeys ? [nextObsKeys] : []);
    setSearchFiles(
      nextObsKeys
        ? [createHistorySearchFile(nextObsKeys, item.filename)]
        : []
    );

    const res = await searchMutation.mutateAsync({
      obskey: nextObsKeys,
      keywords: nextKeywords,
      tags: nextTags,
      page_index: 1,
      page_size: 10,
    });

    setResults(res);
    setResultList(res?.results || []);
    setPageIndex(1);
    setHasNext(!!res?.has_next);
  }

  const searchBoxProps = {
    keywords,
    setKeywords,
    tags,
    setTags,
    loading: searchMutation.isLoading || uploadingFile,
    files: searchFiles,
    onSearch: handleSearch,
    onAddTag: () => setTagModalOpen(true),
    onFileBeforeUpload: handleFileBeforeUpload,
    onRemoveFile: handleRemoveFile,
    onPasteFile: handlePasteFile,
    onReset: () => {
      resetSearch(true);

      setKeywords('');
      setTags([]);
      setSearchFiles([]);
      setObsKeys([]);
    }
  };

  return (
    <div
      className={[
        'drive-search-overlay',
        open ? 'drive-search-overlay--open' : '',
        results ? 'drive-search-overlay--result' : ''
      ].filter(Boolean).join(' ')}
    >
      <Button
        icon={<CloseOutlined/>}
        onClick={handleClose}
        className="drive-search-overlay__close"
      />

      <div className="drive-search-overlay__inner">
        {/*<h2 className="drive-search-overlay__title">搜索一下</h2>*/}

        {!results ? (
          <>
            <SearchBox {...searchBoxProps}/>

            <div className="drive-search-recents">
              {recentList.map((item: any, index: number) => (
                <RecentSearchCard
                  key={item.id || index}
                  item={item}
                  index={index}
                  onClick={() => {
                    void handleRecentSearch(item);
                  }}
                />
              ))}
            </div>

            {!!recentList.length && (
              <div className="drive-search-recents__footer">
                <Button
                  type="link"
                  size="small"
                  onClick={handleClearHistory}
                >
                  清空历史
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="drive-search-overlay__result-layout">
            <div
              ref={resultContainerRef}
              className="drive-search-results-wrap"
            >
              <SearchResults
                list={resultList}
                loadingMore={loadingMore}
                hasMore={hasNext}
                total={results?.total || null}
                onJumpPath={(e) => {
                  handleClose();
                  onJumpPath(e);
                }}
              />
            </div>

            <div className="drive-search-overlay__result-search">
              <SearchBox {...searchBoxProps}/>
            </div>
          </div>
        )}

        <TagPickerModal
          open={tagModalOpen}
          value={tags}
          onCancel={() => setTagModalOpen(false)}
          onOk={(nextTags: any) => {
            setTags(nextTags);
            setTagModalOpen(false);
          }}
        />
      </div>
    </div>
  );
}
